import "./CreditsPopoverComponent.scss";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Coins, ChevronDown, AlertCircle } from "lucide-react";
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { useMemo, useState } from "react";
import TopupPlansDialog from "@/components/common-components/topup-plans-dialog/TopupPlansDialog";
import FeatureRestrictionWrapper from "@/components/common-components/feature-restriction/FeatureRestrictionWrapper";

interface CreditsPopoverComponentProps {
    onRedeemCode?: () => void;
    onBuyCredits?: () => void;
    className?: string;
}

const CreditsPopoverComponent = ({ 
    onBuyCredits,
    className = ""
}: CreditsPopoverComponentProps) => {
    
    const [isTopupDialogOpen, setIsTopupDialogOpen] = useState(false);
    
    // Get user balance data from Redux store
    const utilization = useSelector((state: IRootReducerState) => state.meta.usersBalance);
    const balanceLoading = useSelector((state: IRootReducerState) => state.meta.usersBalanceLoading);
    const balanceError = useSelector((state: IRootReducerState) => state.meta.usersBalanceError);
    
    // Calculate credit values from the real data
    const creditData = useMemo(() => {
        const totalBudget = utilization?.total_budget ?? 0;
        const usedBudget = utilization?.user_used_budget ?? 0;
        const remainingBudget = utilization?.user_remaining_budget ?? 0;
        const giftCredits = utilization?.gift_credits ?? 0;
        const subscriptionCredits = utilization?.subscription_credits ?? 0;
        const topupCredits = utilization?.topup_credits ?? 0;
        
        return {
            totalCredits: totalBudget,
            usedCredits: usedBudget,
            remainingCredits: remainingBudget,
            giftCredits: giftCredits,
            subscriptionCredits: subscriptionCredits,
            topupCredits: topupCredits
        };
    }, [utilization]);

    const handleBuyCredits = () => {
        if (onBuyCredits) {
            onBuyCredits();
        }
        setIsTopupDialogOpen(true);
    };

    const handleCloseTopupDialog = () => {
        setIsTopupDialogOpen(false);
    };

    const totalCredits = creditData.remainingCredits;

    console.log("balanceLoading", balanceLoading);
    return (
        <>
        <Popover>
            <PopoverTrigger asChild>
                <Button 
                    variant="text" 
                    className={`credits-trigger text-secondary ${className}`}
                >
                    <Coins className="h-4 w-4 mr-1" />
                    {balanceLoading ? (
                        <Skeleton className="h-4 w-12 rounded" />
                    ) : balanceError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                    ) : (
                        <span className="credits-amount">{totalCredits.toFixed(2)}</span>
                    )}
                    <ChevronDown strokeWidth={1} size={16} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 mt-2 mr-4 credits-popover-container">
                <div className="credits-popover-content">
                    {/* Credit Balance Section */}
                    <div className="credit-balance-section">
                        <h3 className="credit-balance-title">Credit Balance</h3>
                        {balanceLoading ? (
                            <div className="loading-state space-y-3">
                                <div className="flex items-center justify-between">
                                    <Skeleton className="h-4 w-28" />
                                    <Skeleton className="h-6 w-16" />
                                </div>
                            </div>
                        ) : balanceError ? (
                            <div className="error-state">
                                <AlertCircle className="h-4 w-4 text-destructive mx-auto mb-2" />
                                <p className="text-center text-sm text-destructive">Failed to load balance</p>
                                <p className="text-center text-xs text-muted-foreground">{balanceError}</p>
                            </div>
                        ) : (
                            <div className="credit-balance-content">
                                {/* Main Credits Display */}
                                <div className="main-credits">
                                    {/* <div className="credit-item primary expandable">
                                        <span className="credit-label">Total Credits</span>
                                        <div className="credit-value-with-arrow">
                                            <span className="credit-value large">{creditData.totalCredits.toFixed(2)}</span>
                                            <button 
                                                className="expand-toggle"
                                                onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
                                            >
                                                {isBreakdownExpanded ? (
                                                    <ChevronUp className="h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {isBreakdownExpanded && (
                                        <div className="breakdown-content">
                                            <div className="credit-item breakdown">
                                                <span className="credit-label">Used Credits</span>
                                                <span className="credit-value">{creditData.usedCredits.toFixed(2)}</span>
                                            </div>
                                            <div className="credit-item breakdown">
                                                <span className="credit-label">Gift Credits</span>
                                                <span className="credit-value">{creditData.giftCredits.toFixed(2)}</span>
                                            </div>
                                            <div className="credit-item breakdown">
                                                <span className="credit-label">Subscription Credits</span>
                                                <span className="credit-value">{creditData.subscriptionCredits.toFixed(2)}</span>
                                            </div>
                                            <div className="credit-item breakdown">
                                                <span className="credit-label">Top-up Credits</span>
                                                <span className="credit-value">{creditData.topupCredits.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )} */}
                                    
                                    <div className="credit-item primary">
                                        <span className="credit-label">Remaining Credits</span>
                                        <span className="credit-value large">{creditData.remainingCredits.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons Section */}
                    {!balanceLoading && !balanceError && (
                        <div className="credits-actions-section">
                            <FeatureRestrictionWrapper
                                feature="credit_topup"
                                showUpgradePrompt={true}
                            >
                                <Button
                                    variant="default"
                                    className="buy-credits-button w-full justify-center"
                                    onClick={handleBuyCredits}
                                    mixpanelEvent={{ name: "buy_credits_button_clicked" }}
                                >
                                    <Coins className="h-4 w-4 mr-2" />
                                    Buy Credits
                                </Button>
                            </FeatureRestrictionWrapper>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>

        {/* Topup Plans Dialog */}
        <TopupPlansDialog
            isOpen={isTopupDialogOpen}
            onClose={handleCloseTopupDialog}
        />
        </>
    );
};

export default CreditsPopoverComponent;
